import { getFlacData, makeFlacBox } from '../flac/flacInMp4';
import { AdtsStreamParser } from '../aac/adts';
import { makeError } from '../util/exception';

// @ts-expect-error - For some reason the type definitions for mux.js are not working properly
import { Transmuxer } from './muxJsFork/mp4/transmuxer';

import type { TransmuxerOps, TransmuxerData, Flac, SampleInfo } from './transmuxTypes';
import { encodeBox } from '../mp4/encoder';
import { BitWriter } from '../flac/bitWriter';
import { invariant } from '../util/invariant';
import { safeArrayAccess } from '../util/safeArrayAccess';
import { convertFloatPcmToS16LE } from '../aac/spectralToSamples';
import type { ObservableState } from '../util/observable';
import type { DecodedPcmFloat32Channels } from '../aac/types';
import type { FragmentedMp4Buffers } from '../mp4/fragmentedMp4Types';

export function downmix(inputChannels: Float32Array[], flacDownmixConfig: FlacDownmixConfig): Float32Array[] {
  if (inputChannels.length === 0) {
    return [new Float32Array(0), new Float32Array(0)];
  }

  const firstChannel = safeArrayAccess(inputChannels, 0, 'inputChannels must have at least one channel');
  const nSamples = firstChannel.length;
  const leftChannel = new Float32Array(nSamples);
  const rightChannel = new Float32Array(nSamples);

  for (let i = 0; i < inputChannels.length; i++) {
    const inputChannel = safeArrayAccess(inputChannels, i, `Missing input channel ${i}`);
    const mixRow = safeArrayAccess(flacDownmixConfig.mixMatrix, i, `Missing mix matrix row ${i}`);
    const leftMix = safeArrayAccess(mixRow, 0, `Missing left mix coefficient for channel ${i}`);
    const rightMix = safeArrayAccess(mixRow, 1, `Missing right mix coefficient for channel ${i}`);

    // TypedArray indexing within validated bounds is always safe - TypeScript's
    // noUncheckedIndexedAccess is overly cautious for Float32Array
    for (let j = 0; j < nSamples; j++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      leftChannel[j]! += leftMix * inputChannel[j]!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rightChannel[j]! += rightMix * inputChannel[j]!;
    }
  }

  return [leftChannel, rightChannel];
}


function getPcmSamples(channels: Float32Array[]): Uint8Array {
  invariant(channels.length === 2, `Expected 2 channels but got ${channels.length}`);
  const channel0 = safeArrayAccess(channels, 0, 'Missing left channel');
  const channel1 = safeArrayAccess(channels, 1, 'Missing right channel');
  invariant(channel0.length === channel1.length, 'Channels should have the same length');

  const bitWriter = new BitWriter();
  // TypedArray indexing within validated bounds is always safe
  for (let i = 0; i < channel0.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sample1 = convertFloatPcmToS16LE(channel0[i]!);
    bitWriter.write16LE(sample1);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sample2 = convertFloatPcmToS16LE(channel1[i]!);
    bitWriter.write16LE(sample2);
  }

  return bitWriter.getBuffer();
}


/**
 * This function decodes the given buffer by re-using the
 * existing parser, if any. The goal is not to lose the
 * AAC context from the previous segment to avoid click
 * artifacts at segment boundaries when playing continuously.
 */
function decodeAacAsPcmChannels(
  adtsStreamBuffer: Uint8Array,
  index: number,
  adtsParsers: AdtsStreamParser[],
): DecodedPcmFloat32Channels {
  if (adtsParsers[index] === undefined) {
    adtsParsers[index] = new AdtsStreamParser(adtsStreamBuffer);
    return adtsParsers[index].decodePcmFloat32Channels();
  } else {
    return adtsParsers[index].decodePacket(adtsStreamBuffer);
  }
}


function decodeAndDownmixAacChannelsToFlac(
  adtsBuffers: Uint8Array[],
  flacDownmixConfig: FlacDownmixConfig,
  adtsParsers: AdtsStreamParser[],
): Flac {
  const decodedSamples: DecodedPcmFloat32Channels[] = [];
  const inputChannels: Float32Array[] = [];

  for (let i = 0; i < adtsBuffers.length; i++) {
    const adtsBuffer = safeArrayAccess(adtsBuffers, i, `Missing ADTS buffer at index ${i}`);
    const decoded = decodeAacAsPcmChannels(adtsBuffer, i, adtsParsers);
    decodedSamples.push(decoded);
    inputChannels.push(...decoded.channels);
  }

  const channels: DecodedPcmFloat32Channels = {
    sampleRateInHz: decodedSamples.length === 0 ? 0 : safeArrayAccess(decodedSamples, 0, 'No decoded samples').sampleRateInHz,
    channels: inputChannels,
  };
  const pcmSamples = downmixAacChannels(channels, flacDownmixConfig);

  const numberOfChannels = 2;
  const sampleRateInHz = channels.sampleRateInHz;

  const flacData = getFlacData(pcmSamples, numberOfChannels, sampleRateInHz, true, true);
  const fLaCBox = encodeBox(makeFlacBox(numberOfChannels, sampleRateInHz, flacData.metadataBlockHeader));
  const samples: SampleInfo[] = [];
  for (const buffer of flacData.sampleBuffers) {
    samples.push({
      size: buffer.byteLength,
      duration: flacData.samplesPerChannelPerFrame,
    });
  }
  return {
    sampleBuffers: flacData.sampleBuffers,
    fLaCBox,
    samples,
  };
}


export function downmixAacChannels(
  channels: DecodedPcmFloat32Channels,
  flacDownmixConfig: FlacDownmixConfig,
): Uint8Array {

  const channelCount = channels.channels.length;
  flacDownmixConfig.audioChannelCount.value = channelCount;

  if (flacDownmixConfig.mixMatrix.length === 0) {
    // If we have no mix matrix, it means that this is the first invocation and that
    // we have no default downmix to apply. In this case, we use a default layout
    // where even channels go to left and odd channels go to right
    for (let i = 0; i < channelCount; i++) {
      const playLeft = (i % 2) === 0;
      flacDownmixConfig.mixMatrix[i] = [playLeft ? 1.0 : 0.0, (!playLeft) ? 1.0 : 0.0];
    }
  } else if (flacDownmixConfig.mixMatrix.length < channelCount) {
    // If the mix matrix that we have has less rows than there are channels in
    // this segment, it means that it was the first invocation of downmixAacChannelsToFlac
    // for this instance of the transmuxer. In this case, the mix matrix we received was built
    // from the default downmix, if any so we just extend it by adding the missing channels
    // as muted
    for (let i = flacDownmixConfig.mixMatrix.length; i < channelCount; i++) {
      flacDownmixConfig.mixMatrix[i] = [0.0, 0.0];
    }
  }

  if (flacDownmixConfig.rawInputChannels !== undefined) {
    flacDownmixConfig.rawInputChannels.value = channels;
  }
  const outputChannels = downmix(channels.channels, flacDownmixConfig);
  const pcmSamples = getPcmSamples(outputChannels);
  if (flacDownmixConfig.rawPcmSamples !== undefined) {
    flacDownmixConfig.rawPcmSamples.value = pcmSamples;
  }

  return pcmSamples;
}




export interface FlacDownmixConfig {
  /**
   * For debug purpose
   */
  playerId: string;

  /**
   * The downmix matrix to apply or an empty array if this is the first invocation
   * before we we know the number of channels
   */
  mixMatrix: number[][];

  audioChannelCount: ObservableState<number | null>;

  /**
   * For test purpose
   */
  rawPcmSamples?: ObservableState<Uint8Array | null>;

  /**
   * When we want to extract the PCM samples for each audio track.
   */
  rawInputChannels?: ObservableState<DecodedPcmFloat32Channels | null>;
}

interface TransmuxStatePending {
  type: 'pending',
  info: Record<string, any>,
}

interface TransmuxStateBuffer {
  type: 'buffers',
  buffers: FragmentedMp4Buffers,
}

interface TransmuxStateIdle {
  type: 'idle',
}

type TransmuxState = TransmuxStateIdle | TransmuxStatePending | TransmuxStateBuffer;


export interface TransmuxUtilityOps {

  transmuxSegment(
    m2tsSegment: Uint8Array,
    info: Record<string, any>,
    flacDownmixConfig: FlacDownmixConfig | null,
    onlyUseLastAacPacket: boolean,
  ): FragmentedMp4Buffers;

}


export class TransmuxUtility implements TransmuxUtilityOps {
  transmuxer: TransmuxerOps;
  state: TransmuxState = { type: 'idle' };

  private adtsParsers: AdtsStreamParser[] = [];

  constructor() {
    this.transmuxer = new Transmuxer({}) as any;

    this.transmuxer.on('data', (segment: TransmuxerData) => {
      if (this.state.type !== 'pending') {
        const err = makeError('Unexpected segment from transmuxer', 'StateError', { info: { segment } });
        console.error('Transmuxer', err);
        throw err;
      }

      this.state = {
        type: 'buffers',
        buffers: {
          info: this.state.info,
          initialization: segment.initSegment,
          mediaSegment: segment.data,
        }
      };
    });
  }

  transmuxSegment(
    m2tsSegment: Uint8Array,
    info: Record<string, any>,
    flacDownmixConfig: FlacDownmixConfig | null,
    onlyUseLastAacPacket = false,
  ): FragmentedMp4Buffers {
    if (this.state.type !== 'idle') {
      throw makeError('Previous transmux operation not yet completed', 'StateError', { info });
    }

    this.state = { type: 'pending', info };

    const downmixFn = flacDownmixConfig !== null
      ? (adtsBuffers: Uint8Array[]) => decodeAndDownmixAacChannelsToFlac(adtsBuffers, flacDownmixConfig, this.adtsParsers)
      : undefined;
    this.transmuxer.setDownmixAacChannelsToFlacFn(downmixFn);
    this.transmuxer.push(m2tsSegment);
    this.transmuxer.flush(onlyUseLastAacPacket);
    return this.getResult(info);
  }

  private getResult(info: Record<string, any>): FragmentedMp4Buffers {
    if (this.state.type === 'pending') {
      throw makeError('Segment produced no fragment', 'StateError', { info });
    }
    if (this.state.type === 'idle') {
      throw makeError('Unexpected idle state after transmux', 'StateError', { info });
    }
    const result = this.state.buffers;
    this.state = { type: 'idle' };
    return result;
  }

  close() {
    this.transmuxer.off('data');
  }
}
